import { create } from "zustand";
import { v4 } from "uuid";
import type { TAvatarSettings, AvatarQuality, VoiceEmotion } from "@/types/TAvatarSettings";
import type { TAvatar } from "@/types/TAvatar";
import fetchSettings from "@/services/queries/fetchSettings";
import { fetchPartialSettings } from "@/services/queries/fetchPartialSettings";
import updateSettings from "@/services/queries/updateSettings";

interface SettingsState extends TAvatarSettings {
  isLoading: boolean;
  error: string;
  fetchedSettings: TAvatarSettings | undefined;
  hasChanges: boolean;

  // Setter methods
  setIsLoading: (isLoading: boolean) => void;
  setAvatars: (avatars: TAvatar[]) => void;
  setLanguage: (language: string) => void;
  setUsePushToTalk: (use: boolean) => void;
  setDisplayHistory: (display: boolean) => void;
  setDisplayMessages: (display: boolean) => void;
  setWelcomeMessage: (message: string) => void;
  setSystemMessage: (message: string) => void;
  setUseAvatar: (use: boolean) => void;
  setUseKnowledgeBase: (use: boolean) => void;
  setUseSchadenKI: (use: boolean) => void;
  setAvatarQuality: (quality: AvatarQuality) => void;
  setVoiceSpeedRate: (rate: number) => void;
  setVoiceEmotion: (emotion: VoiceEmotion) => void;
  setMaintenanceMode: (maintenanceMode: boolean) => void;
  setBrandingLogoUrl: (url: string) => void;
  setBrandingPlayLogoUrl: (url: string) => void;
  setBrandingPlayLogoHoverUrl: (url: string) => void;
  setBrandingTitle: (title: string) => void;
  setBrandingFaviconUrl: (url: string) => void;
  setBrandingPrimaryColor: (color: string) => void;
  setBrandingCtaColor: (color: string) => void;
  setBrandingAccentColor: (color: string) => void;
  setBrandingUserMsgBg: (color: string) => void;
  setBrandingBotMsgBg: (color: string) => void;
  setBrandingUserMsgText: (color: string) => void;
  setBrandingBotMsgText: (color: string) => void;
  setRealtimeEndpoint: (endpoint: string) => void;
  setRealtimeDeployment: (deployment: string) => void;
  setRealtimeApiKey: (key: string) => void;
  setRealtimeApiVersion: (version: string) => void;
  setRealtimeWebSocketUrl: (url: string) => void;

  // Helper methods
  calculateHasChanges: () => boolean;

  // API methods
  fetchSettings: () => Promise<void>;
  fetchPartialSettings: () => Promise<void>;
  saveSettings: () => Promise<void>;
}

const SettingsStore = create<SettingsState>((set, get) => ({
  isLoading: false,
  error: "",
  id: v4(),
  type: "AVATAR_SETTINGS",
  avatars: [],
  language: "de",
  usePushToTalk: false,
  displayHistory: false,
  displayMessages: true,
  welcomeMessage: "Willkommen! Wie kann ich Ihnen helfen?",
  avatarQuality: "medium",
  voiceSpeedRate: 1,
  voiceEmotion: "friendly",
  fetchedSettings: undefined,
  systemMessage: `You are a helpful assistant specializing in answering questions based on the available knowledge base.

IMPORTANT GUIDELINES:
- Always maintain a professional, friendly, and helpful tone.
- If a user asks about topics outside your area of expertise, politely let them know and redirect them.

When the user asks for information:
1. Use the 'search' tool to check the knowledge base before answering.
2. Use the 'report_grounding' tool to report the source of information.
3. Provide accurate, clear, and concise answers based on the available documentation.

User Management:
- After authenticating the user, call the 'get_user' tool to fetch user information.
- After updating user info by using 'update_user' tool, call the 'get_user' tool again to fetch updated changes.`,
  useAvatar: true,
  useKnowledgeBase: true,
  useSchadenKI: false,
  maintenanceMode: false,
  brandingLogoUrl: "",
  brandingPlayLogoUrl: "",
  brandingPlayLogoHoverUrl: "",
  brandingTitle: "",
  brandingFaviconUrl: "",
  brandingPrimaryColor: "",
  brandingCtaColor: "",
  brandingAccentColor: "",
  brandingUserMsgBg: "",
  brandingBotMsgBg: "",
  brandingUserMsgText: "",
  brandingBotMsgText: "",
  realtimeEndpoint: "",
  realtimeDeployment: "",
  realtimeApiKey: "",
  realtimeApiVersion: "2024-12-17",
  realtimeWebSocketUrl: "",
  hasChanges: false,

  // Setters
  setIsLoading: (isLoading: boolean) => set({ isLoading }),
  setAvatars: (avatars: TAvatar[]) => {
    set({ avatars });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setLanguage: (language: string) => {
    set({ language });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setUsePushToTalk: (usePushToTalk: boolean) => {
    set({ usePushToTalk });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setDisplayHistory: (displayHistory: boolean) => {
    set({ displayHistory });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setDisplayMessages: (displayMessages: boolean) => {
    set({ displayMessages });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setWelcomeMessage: (welcomeMessage: string) => {
    set({ welcomeMessage });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setSystemMessage: (systemMessage: string) => {
    set({ systemMessage });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setUseAvatar: (useAvatar: boolean) => {
    set({ useAvatar });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setUseKnowledgeBase: (useKnowledgeBase: boolean) => {
    // When enabling knowledge base, disable Schaden KI (mutually exclusive)
    if (useKnowledgeBase) {
      set({ useKnowledgeBase, useSchadenKI: false });
    } else {
      set({ useKnowledgeBase });
    }
    set({ hasChanges: get().calculateHasChanges() });
  },
  setUseSchadenKI: (useSchadenKI: boolean) => {
    // When enabling Schaden KI, disable knowledge base (mutually exclusive)
    if (useSchadenKI) {
      set({ useSchadenKI, useKnowledgeBase: false });
    } else {
      set({ useSchadenKI });
    }
    set({ hasChanges: get().calculateHasChanges() });
  },
  setAvatarQuality: (avatarQuality: AvatarQuality) => {
    set({ avatarQuality });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setVoiceSpeedRate: (voiceSpeedRate: number) => {
    set({ voiceSpeedRate });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setVoiceEmotion: (voiceEmotion: VoiceEmotion) => {
    set({ voiceEmotion });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setMaintenanceMode: (maintenanceMode: boolean) => {
    set({ maintenanceMode });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingLogoUrl: (brandingLogoUrl: string) => {
    set({ brandingLogoUrl });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingPlayLogoUrl: (brandingPlayLogoUrl: string) => {
    set({ brandingPlayLogoUrl });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingPlayLogoHoverUrl: (brandingPlayLogoHoverUrl: string) => {
    set({ brandingPlayLogoHoverUrl });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingTitle: (brandingTitle: string) => {
    set({ brandingTitle });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingFaviconUrl: (brandingFaviconUrl: string) => {
    set({ brandingFaviconUrl });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingPrimaryColor: (brandingPrimaryColor: string) => {
    set({ brandingPrimaryColor });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingCtaColor: (brandingCtaColor: string) => {
    set({ brandingCtaColor });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingAccentColor: (brandingAccentColor: string) => {
    set({ brandingAccentColor });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingUserMsgBg: (brandingUserMsgBg: string) => {
    set({ brandingUserMsgBg });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingBotMsgBg: (brandingBotMsgBg: string) => {
    set({ brandingBotMsgBg });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingUserMsgText: (brandingUserMsgText: string) => {
    set({ brandingUserMsgText });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setBrandingBotMsgText: (brandingBotMsgText: string) => {
    set({ brandingBotMsgText });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setRealtimeEndpoint: (realtimeEndpoint: string) => {
    set({ realtimeEndpoint });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setRealtimeDeployment: (realtimeDeployment: string) => {
    set({ realtimeDeployment });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setRealtimeApiKey: (realtimeApiKey: string) => {
    set({ realtimeApiKey });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setRealtimeApiVersion: (realtimeApiVersion: string) => {
    set({ realtimeApiVersion });
    set({ hasChanges: get().calculateHasChanges() });
  },
  setRealtimeWebSocketUrl: (realtimeWebSocketUrl: string) => {
    set({ realtimeWebSocketUrl });
    set({ hasChanges: get().calculateHasChanges() });
  },

  calculateHasChanges: () => {
    const state = get();
    const { fetchedSettings } = state;
    
    if (!fetchedSettings) return true;
    
    return JSON.stringify({
      avatars: state.avatars,
      language: state.language,
      usePushToTalk: state.usePushToTalk,
      displayHistory: state.displayHistory,
      displayMessages: state.displayMessages,
      welcomeMessage: state.welcomeMessage,
      systemMessage: state.systemMessage,
      useAvatar: state.useAvatar,
      useKnowledgeBase: state.useKnowledgeBase,
      useSchadenKI: state.useSchadenKI,
      avatarQuality: state.avatarQuality,
      voiceSpeedRate: state.voiceSpeedRate,
      voiceEmotion: state.voiceEmotion,
      maintenanceMode: state.maintenanceMode,
      brandingLogoUrl: state.brandingLogoUrl,
      brandingPlayLogoUrl: state.brandingPlayLogoUrl,
      brandingPlayLogoHoverUrl: state.brandingPlayLogoHoverUrl,
      brandingTitle: state.brandingTitle,
      brandingFaviconUrl: state.brandingFaviconUrl,
      brandingPrimaryColor: state.brandingPrimaryColor,
      brandingCtaColor: state.brandingCtaColor,
      brandingAccentColor: state.brandingAccentColor,
      brandingUserMsgBg: state.brandingUserMsgBg,
      brandingBotMsgBg: state.brandingBotMsgBg,
      brandingUserMsgText: state.brandingUserMsgText,
      brandingBotMsgText: state.brandingBotMsgText,
      realtimeEndpoint: state.realtimeEndpoint,
      realtimeDeployment: state.realtimeDeployment,
      realtimeApiKey: state.realtimeApiKey,
      realtimeApiVersion: state.realtimeApiVersion,
      realtimeWebSocketUrl: state.realtimeWebSocketUrl,
    }) !== JSON.stringify({
      avatars: fetchedSettings.avatars,
      language: fetchedSettings.language,
      usePushToTalk: fetchedSettings.usePushToTalk,
      displayHistory: fetchedSettings.displayHistory,
      displayMessages: fetchedSettings.displayMessages,
      welcomeMessage: fetchedSettings.welcomeMessage,
      systemMessage: fetchedSettings.systemMessage,
      useAvatar: fetchedSettings.useAvatar,
      useKnowledgeBase: fetchedSettings.useKnowledgeBase,
      useSchadenKI: fetchedSettings.useSchadenKI,
      avatarQuality: fetchedSettings.avatarQuality,
      voiceSpeedRate: fetchedSettings.voiceSpeedRate,
      voiceEmotion: fetchedSettings.voiceEmotion,
      maintenanceMode: fetchedSettings.maintenanceMode,
      brandingLogoUrl: fetchedSettings.brandingLogoUrl,
      brandingPlayLogoUrl: fetchedSettings.brandingPlayLogoUrl,
      brandingPlayLogoHoverUrl: fetchedSettings.brandingPlayLogoHoverUrl,
      brandingTitle: fetchedSettings.brandingTitle,
      brandingFaviconUrl: fetchedSettings.brandingFaviconUrl,
      brandingPrimaryColor: fetchedSettings.brandingPrimaryColor,
      brandingCtaColor: fetchedSettings.brandingCtaColor,
      brandingAccentColor: fetchedSettings.brandingAccentColor,
      brandingUserMsgBg: fetchedSettings.brandingUserMsgBg,
      brandingBotMsgBg: fetchedSettings.brandingBotMsgBg,
      brandingUserMsgText: fetchedSettings.brandingUserMsgText,
      brandingBotMsgText: fetchedSettings.brandingBotMsgText,
      realtimeEndpoint: fetchedSettings.realtimeEndpoint,
      realtimeDeployment: fetchedSettings.realtimeDeployment,
      realtimeApiKey: fetchedSettings.realtimeApiKey,
      realtimeApiVersion: fetchedSettings.realtimeApiVersion,
      realtimeWebSocketUrl: fetchedSettings.realtimeWebSocketUrl,
    });
  },

  // API methods
  fetchSettings: async () => {
    set({
      isLoading: true,
      error: "",
    });

    try {
      const settings = await fetchSettings<TAvatarSettings>();
      if (settings) {
        // Ensure all avatars have a weight property for backward compatibility
        const avatarsWithWeight = settings.avatars?.map(avatar => ({
          ...avatar,
          weight: avatar.weight ?? 1.0
        })) || [];
        
        set({
          ...settings,
          avatars: avatarsWithWeight,
          fetchedSettings: { ...settings, avatars: avatarsWithWeight },
          hasChanges: false,
        });
      }
    } catch (error) {
      set({
        error: (error as Error)?.message || "Failed to load settings",
      });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchPartialSettings: async () => {
    set({
      isLoading: true,
      error: "",
    });

    try {
      const partialSettings = await fetchPartialSettings<Partial<TAvatarSettings>>();
      if (partialSettings) {
        // Ensure all avatars have a weight property for backward compatibility
        const avatarsWithWeight = partialSettings.avatars?.map(avatar => ({
          ...avatar,
          weight: avatar.weight ?? 1.0
        }));
        
        set((state) => ({
          ...state,
          ...partialSettings,
          ...(avatarsWithWeight ? { avatars: avatarsWithWeight } : {}),
          isLoading: false,
        }));
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      set({
        error: (error as Error)?.message || "Failed to load partial settings",
        isLoading: false,
      });
    }
  },

  saveSettings: async () => {
    set({
      isLoading: true,
      error: "",
    });

    try {
      const {
        id,
        type,
        avatars,
        language,
        usePushToTalk,
        displayHistory,
        displayMessages,
        welcomeMessage,
        systemMessage,
        useAvatar,
        useKnowledgeBase,
        useSchadenKI,
        avatarQuality,
        voiceSpeedRate,
        voiceEmotion,
        maintenanceMode,
        brandingLogoUrl,
        brandingPlayLogoUrl,
        brandingPlayLogoHoverUrl,
        brandingTitle,
        brandingFaviconUrl,
        brandingPrimaryColor,
        brandingCtaColor,
        brandingAccentColor,
        brandingUserMsgBg,
        brandingBotMsgBg,
        brandingUserMsgText,
        brandingBotMsgText,
        realtimeEndpoint,
        realtimeDeployment,
        realtimeApiKey,
        realtimeApiVersion,
        realtimeWebSocketUrl,
      } = get();

      const settings = {
        id,
        type,
        avatars,
        language,
        usePushToTalk,
        displayHistory,
        displayMessages,
        welcomeMessage,
        systemMessage,
        useAvatar,
        useKnowledgeBase,
        useSchadenKI,
        avatarQuality,
        voiceSpeedRate,
        voiceEmotion,
        maintenanceMode,
        brandingLogoUrl,
        brandingPlayLogoUrl,
        brandingPlayLogoHoverUrl,
        brandingTitle,
        brandingFaviconUrl,
        brandingPrimaryColor,
        brandingCtaColor,
        brandingAccentColor,
        brandingUserMsgBg,
        brandingBotMsgBg,
        brandingUserMsgText,
        brandingBotMsgText,
        realtimeEndpoint,
        realtimeDeployment,
        realtimeApiKey,
        realtimeApiVersion,
        realtimeWebSocketUrl,
      };

      const savedSettings = await updateSettings(settings);
      
      set({
        ...savedSettings,
        fetchedSettings: savedSettings,
        hasChanges: false,
      });

      // Return success
      return Promise.resolve();
    } catch (error) {
      set({
        error: (error as Error)?.message || "Failed to save settings",
      });
      // Return error
      return Promise.reject(error);
    } finally {
      set({ isLoading: false });
    }
  },
}));

export default SettingsStore;
